import { useEffect, useMemo, useState } from "react";
import { getJson, nf, card, control, sectionLabel } from "../kb/shared";
import { CHART_LABEL, WIDTHS, validCharts, queryUrl, autoTitle, useQuery, WidgetBody, scopeParam } from "./Widget";

// Modal for building or editing a widget, with a live preview of the result.

function FilterRow({ f, ds, ui, onChange, onRemove }) {
  const { B, fs } = ui;
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [labels, setLabels] = useState(f.labels || {});

  useEffect(() => {
    if (!f.dim) return;
    let live = true;
    const t = setTimeout(() => {
      getJson(`/api/dash/values?dataset=${encodeURIComponent(ds.key)}&dim=${encodeURIComponent(f.dim)}&q=${encodeURIComponent(q)}`)
        .then(d => live && setOptions(d.values || []))
        .catch(() => live && setOptions([]));
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [ds.key, f.dim, q]);

  const add = (v) => {
    if (f.values.includes(String(v.key))) return;
    const nextLabels = { ...labels, [v.key]: v.label };
    setLabels(nextLabels);
    onChange({ ...f, values: [...f.values, String(v.key)], labels: nextLabels });
  };
  const removeValue = (k) => onChange({ ...f, values: f.values.filter(x => x !== k) });

  return (
    <div style={{ ...card(ui), padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={f.dim} onChange={e => onChange({ dim: e.target.value, values: [], exclude: false, labels: {} })} style={{ ...control(ui), flex: "1 1 140px" }}>
          {ds.dims.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        <select value={f.exclude ? "exclude" : "include"} onChange={e => onChange({ ...f, exclude: e.target.value === "exclude" })} style={control(ui)}>
          <option value="include">is any of</option>
          <option value="exclude">is none of</option>
        </select>
        <button onClick={onRemove} aria-label="Remove filter" style={{ ...control(ui), cursor: "pointer" }}>✕</button>
      </div>
      {f.values.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {f.values.map(k => (
            <span key={k} style={{ fontSize: fs(11), color: B.text, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 14, padding: "2px 4px 2px 9px", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {labels[k] || k}
              <button onClick={() => removeValue(k)} aria-label={`Remove ${labels[k] || k}`} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(12), padding: "0 3px" }}>×</button>
            </span>
          ))}
        </div>
      )}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search values…" style={control(ui)} />
      <div style={{ maxHeight: 130, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {options.filter(o => !f.values.includes(String(o.key))).slice(0, 25).map(o => (
          <button key={o.key} onClick={() => add(o)} style={{ display: "flex", justifyContent: "space-between", gap: 10, background: "none", border: "none", padding: "5px 6px", cursor: "pointer", color: B.textMuted, fontSize: fs(12), textAlign: "left", fontFamily: "'Satoshi', sans-serif", borderRadius: 4 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
            <span style={{ color: B.textDim, fontVariantNumeric: "tabular-nums" }}>{nf(o.count)}</span>
          </button>
        ))}
        {options.length === 0 && <span style={{ fontSize: fs(11), color: B.textDim, padding: 6 }}>No values.</span>}
      </div>
    </div>
  );
}

export function WidgetEditor({ initial, schema, range, focus, ui, onSave, onCancel }) {
  const { B, fs, HP } = ui;
  const [w, setW] = useState(() => initial || { dataset: schema[0].key, measure: schema[0].measures[0].key, groupBy: "", filters: [], chart: "stat", limit: 10, width: 4, title: "" });
  const ds = schema.find(d => d.key === w.dataset) || schema[0];
  const dim = ds.dims.find(d => d.key === w.groupBy);
  const charts = validCharts(dim?.kind);
  const chart = charts.includes(w.chart) ? w.chart : charts[0];
  const draft = useMemo(() => ({ ...w, chart }), [w, chart]);
  const preview = useQuery(queryUrl(draft, range, focus), 0);

  const set = (patch) => setW(prev => ({ ...prev, ...patch }));
  const pickDataset = (key) => {
    const next = schema.find(d => d.key === key);
    set({ dataset: key, measure: next.measures[0].key, groupBy: "", filters: [], chart: "stat" });
  };
  const pickGroupBy = (key) => {
    const kind = ds.dims.find(d => d.key === key)?.kind;
    const opts = validCharts(kind);
    set({ groupBy: key, chart: opts[0], width: !key ? 4 : opts[0] === "matrix" ? 12 : opts[0] === "map" || opts[0] === "column" ? 8 : 6 });
  };

  const label = { ...sectionLabel(ui), display: "block", marginBottom: 6 };
  const field = { display: "flex", flexDirection: "column", gap: 4 };

  return (
    <div role="dialog" aria-modal="true" aria-label={initial ? "Edit widget" : "Add widget"} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4vh 16px", overflowY: "auto" }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...card(ui), background: B.bgPanel, width: "100%", maxWidth: 1180, padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <div style={{ fontSize: fs(18), fontWeight: 900, color: B.text }}>{initial ? "Edit widget" : "Add a widget"}</div>

          <div style={field}>
            <span style={label}>Data</span>
            <select value={w.dataset} onChange={e => pickDataset(e.target.value)} style={control(ui)}>
              {schema.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <span style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.5 }}>{ds.description}</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={field}>
              <span style={label}>Measure</span>
              <select value={w.measure} onChange={e => set({ measure: e.target.value })} style={control(ui)}>
                {ds.measures.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div style={field}>
              <span style={label}>Group by</span>
              <select value={w.groupBy} onChange={e => pickGroupBy(e.target.value)} style={control(ui)}>
                <option value="">Nothing — one number</option>
                {ds.dims.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
          </div>

          <div style={field}>
            <span style={label}>Chart</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {charts.map(c => (
                <button key={c} onClick={() => set({ chart: c })} style={{ ...control(ui), cursor: "pointer", fontWeight: chart === c ? 700 : 500, color: chart === c ? B.text : B.textMuted, borderColor: chart === c ? B.primary + "88" : B.border, background: chart === c ? B.primary + "18" : B.bgCard }}>{CHART_LABEL[c]}</button>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {(chart === "bar" || chart === "table") && (
              <div style={field}>
                <span style={label}>Show top</span>
                <select value={w.limit} onChange={e => set({ limit: Number(e.target.value) })} style={control(ui)}>
                  {[5, 10, 15, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            <div style={field}>
              <span style={label}>Size</span>
              <select value={w.width} onChange={e => set({ width: Number(e.target.value) })} style={control(ui)}>
                {WIDTHS.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
              </select>
            </div>
          </div>

          <div style={field}>
            <span style={label}>Filters</span>
            {w.filters.map((f, i) => (
              <FilterRow key={i} f={f} ds={ds} ui={ui}
                onChange={nf2 => set({ filters: w.filters.map((x, j) => (j === i ? nf2 : x)) })}
                onRemove={() => set({ filters: w.filters.filter((_, j) => j !== i) })} />
            ))}
            <button onClick={() => set({ filters: [...w.filters, { dim: ds.dims.find(d => !d.kind.startsWith("time"))?.key || ds.dims[0].key, values: [], exclude: false, labels: {} }] })}
              style={{ ...control(ui), cursor: "pointer", alignSelf: "flex-start" }}>+ Add filter</button>
          </div>

          {scopeParam(focus) && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: fs(12), color: B.textMuted, cursor: "pointer", lineHeight: 1.5 }}>
              <input type="checkbox" checked={!w.ignoreFocus} onChange={e => set({ ignoreFocus: !e.target.checked })} style={{ marginTop: 3 }} />
              <span>Follow the dashboard focus ({[...(focus.threats || []), ...(focus.countries || []), ...(focus.sectors || [])].map(x => x.label).join(", ")})</span>
            </label>
          )}

          <div style={field}>
            <span style={label}>Title</span>
            <input value={w.title} onChange={e => set({ title: e.target.value })} placeholder={autoTitle(draft, schema)} style={control(ui)} />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button onClick={onCancel} style={{ ...HP.secondaryBtn, padding: "9px 18px" }}>Cancel</button>
            <button onClick={() => onSave({ ...draft, filters: draft.filters.filter(f => f.values.length) })} style={{ ...HP.primaryBtn, padding: "9px 18px" }}>{initial ? "Save widget" : "Add to dashboard"}</button>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Preview</div>
          <div style={{ ...card(ui), padding: "14px 16px", opacity: preview.loading && preview.data ? 0.6 : 1 }}>
            <div style={{ fontSize: fs(14), fontWeight: 800, color: B.text, marginBottom: 10 }}>{w.title || autoTitle(draft, schema)}</div>
            {preview.error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{preview.error}</div>}
            {preview.data ? <WidgetBody data={preview.data} chart={chart} limit={w.limit} ui={ui} /> : <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading…</div>}
          </div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8, lineHeight: 1.5 }}>
            The preview uses the dashboard's date range. The widget refreshes itself as the data updates.
          </div>
        </div>
      </div>
    </div>
  );
}
